/**
 * Маппинг ключей меню → GraphQL-фильтры и URL filter.* параметры.
 *
 * take     — Не в работе
 * delay    — Отложено (дата отложения ещё в будущем / по текущей семантике меню)
 * contact  — Пора связываться
 * inwork   — группа «В работе»
 * victory  — Завершено успехом
 * loss     — Завершено провалом
 * all      — Все
 *
 * ID статусов соответствуют сиду company_statuses (id 1..8).
 */
import { COMPANY_STATUS_CODE } from 'src/utils/mappers/companyStatus.mapper';

const IN_WORK_STATUS_CODES = [
	COMPANY_STATUS_CODE.LPR_CONTACT,
	COMPANY_STATUS_CODE.COMMERCIAL_PROPOSAL,
	COMPANY_STATUS_CODE.CONTRACT_PREPARATION,
	COMPANY_STATUS_CODE.CONDITIONS_AND_OBJECTIONS,
];

/** Стабильные id из сида company_statuses */
export const COMPANY_MENU_STATUS_IDS_BY_KEY = {
	take: [1],
	delay: [2],
	contact: [2],
	inwork: [3, 4, 5, 6],
	victory: [7],
	loss: [8],
	all: null,
};

export const COMPANY_MENU_FILTERS_BY_KEY = {
	take: {
		statusCode: { eq: [COMPANY_STATUS_CODE.NOT_IN_WORK] },
	},
	delay: {
		statusCode: { eq: [COMPANY_STATUS_CODE.POSTPONED] },
		postponedUntil: { ltDate: 'P0D' },
	},
	contact: {
		statusCode: { eq: [COMPANY_STATUS_CODE.POSTPONED] },
		postponedUntil: { gtDate: 'P0D' },
	},
	inwork: {
		statusCode: { eq: IN_WORK_STATUS_CODES },
	},
	victory: {
		statusCode: { eq: [COMPANY_STATUS_CODE.COMPLETED_WITH_VICTORY] },
	},
	loss: {
		statusCode: { eq: [COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS] },
	},
	all: null,
};

const MENU_EXTRA_FILTERS_BY_KEY = {
	delay: {
		postponedUntil: { ltDate: 'P0D' },
	},
	contact: {
		postponedUntil: { gtDate: 'P0D' },
	},
};

export const COMPANY_MENU_STATUS_LABELS = {
	take: 'Не в работе',
	delay: 'Отложено',
	contact: 'Пора связываться',
	inwork: 'В работе',
	victory: 'Завершено успехом',
	loss: 'Завершено провалом',
	all: 'Все',
};

/** @deprecated используйте COMPANY_MENU_STATUS_IDS_BY_KEY */
export const COMPANY_STATUSES_BY_MENU_KEY = {
	take: [1],
	delay: [2],
	inwork: [3, 4, 5, 6],
	complete: [7, 8],
	victory: [7],
	loss: [8],
};

const appendFilterParams = (params, code, condition) => {
	if (!condition || typeof condition !== 'object') return;

	const [op, value] = Object.entries(condition)[0] ?? [];

	if (!op || value == null) return;

	params.set(`filter.${code}.op`, op);
	params.set(
		`filter.${code}.value`,
		Array.isArray(value) ? value.join(',') : String(value),
	);
};

export const buildCompanyMenuSearch = (menuKey) => {
	const statusIds = COMPANY_MENU_STATUS_IDS_BY_KEY[menuKey];

	if (!statusIds?.length) return '';

	const params = new URLSearchParams();

	appendFilterParams(params, 'status', { eq: statusIds });

	const extra = MENU_EXTRA_FILTERS_BY_KEY[menuKey];

	if (extra) {
		for (const [code, condition] of Object.entries(extra)) {
			appendFilterParams(params, code, condition);
		}
	}

	const query = params.toString();

	return query ? `?${query}` : '';
};

export const buildCompanyMenuUrl = (prefix, menuKey) =>
	`/${prefix}${buildCompanyMenuSearch(menuKey)}`;

export const resolveCompanyMenuKeyFromSearch = (search = '') => {
	const params =
		typeof search === 'string' ? new URLSearchParams(search) : search;

	const statusOp = params.get('filter.status.op');
	const statusValue = params.get('filter.status.value');

	if (statusOp !== 'eq' || !statusValue) return 'all';

	const statusIds = String(statusValue)
		.split(',')
		.map((item) => Number(item.trim()))
		.filter((item) => Number.isFinite(item))
		.sort((a, b) => a - b);

	const postponedOp = params.get('filter.postponedUntil.op');
	const postponedValue = params.get('filter.postponedUntil.value');

	const matchesIds = (key) => {
		const expected = COMPANY_MENU_STATUS_IDS_BY_KEY[key];

		if (!expected?.length) return false;

		const sorted = [...expected].sort((a, b) => a - b);

		return (
			sorted.length === statusIds.length &&
			sorted.every((id, index) => id === statusIds[index])
		);
	};

	if (matchesIds('delay') || matchesIds('contact')) {
		if (postponedOp === 'ltDate' && postponedValue === 'P0D') return 'delay';
		if (postponedOp === 'gtDate' && postponedValue === 'P0D') return 'contact';

		if (statusIds.length === 1) return 'delay';
	}

	for (const key of ['take', 'inwork', 'victory', 'loss']) {
		if (matchesIds(key)) return key;
	}

	if (statusIds.length === 1) {
		for (const key of ['take', 'inwork', 'victory', 'loss', 'delay']) {
			const expected = COMPANY_MENU_STATUS_IDS_BY_KEY[key];

			if (expected?.includes(statusIds[0])) return key;
		}
	}

	return 'all';
};
